declare global {
  interface Window {
    uetq: unknown[]
  }
}

interface ProvidedCookiebot {
  instance: typeof Cookiebot | null
}

export default defineNuxtPlugin({
  name: 'cookiebot',
  setup() {
    const { cookiebot: cookiebotConfig, mode } = useRuntimeConfig().public

    const envIsTracked = mode === 'production' || mode === 'staging'

    if (!envIsTracked) {
      return {
        provide: { cookiebot: { instance: null } },
      }
    }

    // Set the default for Microsoft UET, Cookiebot will handle the rest
    window.uetq = window.uetq || []
    window.uetq.push('consent', 'default', {
      ad_storage: 'denied',
    })

    const userStore = useUserStore()
    const userIsLoggedIn = computed(() => Boolean(userStore.id))

    const { settings } = storeToRefs(useBusinessStore())
    const userIsInUserpilotBlocklist = computed(() => settings.value?.userPilot?.enabled === false)

    // Only enable global tracking logic if environment is tracked, user is logged in and is not part of the blocklist
    const trackingEnabled = computed(() => envIsTracked && userIsLoggedIn.value && !userIsInUserpilotBlocklist.value)

    const cookiebot = reactive<ProvidedCookiebot>({
      instance: null,
    })

    watchEffect(() => {
      const cbInstance = cookiebot.instance
      if (!cbInstance) return

      if (trackingEnabled.value && !(cbInstance.consented || cbInstance.declined)) {
        cbInstance.show()
      } else {
        cbInstance.hide()
      }
    })

    const { onLoaded } = useScript(
      {
        src: 'https://consent.cookiebot.com/uc.js',
        type: 'text/javascript',
        defer: true,
        crossorigin: false,
        'data-cbid': cookiebotConfig.id,
      },
      {
        use() {
          return window.Cookiebot
        },
      }
    )

    onLoaded(instance => {
      cookiebot.instance = instance
    })

    const cookieConsentChangeEvents: Extract<keyof WindowEventMap, `Cookiebot${string}`>[] = [
      'CookiebotOnConsentReady',
      'CookiebotOnAccept',
      'CookiebotOnDecline',
    ]

    cookieConsentChangeEvents.forEach(eventType => {
      window.addEventListener(eventType, () => {
        if (window.Cookiebot) {
          cookiebot.instance = window.Cookiebot
        }
      })
    })

    return {
      provide: {
        cookiebot,
      },
    }
  },
})
